import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls"; 
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  InputAdornment,
  Avatar,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// ---------------------------------------------------------------------------
// API layer
// ---------------------------------------------------------------------------
const client = axios.create({ baseURL: BASE_URL });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

async function fetchStates() {
  const { data } = await client.get("/internship/get-all-states");
  return data;
}

async function fetchCollegesByState(stateId) {
  const { data } = await client.post("/internship/get-colleges-by-state", {
    state_id: stateId,
  });
  return data;
}

async function fetchLeadersSpocsFaculty({ stateId, instituteId } = {}) {
  const { data } = await client.post("/internship/get-all-active-leaders-spocs-faculty", {
    state_id: stateId ?? null,
    institute_id: instituteId ?? null,
  });
  return data;
}

// ---------------------------------------------------------------------------
// StateCollegeFilter Component
// ---------------------------------------------------------------------------
function StateCollegeFilter({ onChange, onSearch }) {
  const [states, setStates] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [stateId, setStateId] = useState("");
  const [instituteId, setInstituteId] = useState("");
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingColleges, setLoadingColleges] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoadingStates(true);
    fetchStates()
      .then((data) => active && setStates(data))
      .catch(() => active && setError("Couldn't load states. Try again."))
      .finally(() => active && setLoadingStates(false));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!stateId) {
      setColleges([]);
      setInstituteId("");
      return;
    }
    let active = true;
    setLoadingColleges(true);
    setError("");
    fetchCollegesByState(stateId)
      .then((data) => active && setColleges(data))
      .catch(() => active && setError("Couldn't load colleges for that state."))
      .finally(() => active && setLoadingColleges(false));
    return () => { active = false; };
  }, [stateId]);

  const handleStateChange = (e) => {
    const value = e.target.value;
    setStateId(value);
    setInstituteId("");
    onChange({ stateId: value || null, instituteId: null });
  };

  const handleCollegeChange = (e) => {
    const value = e.target.value;
    setInstituteId(value);
    onChange({ stateId: stateId || null, instituteId: value || null });
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <FormControl size="small" className="w-full sm:w-64">
        <InputLabel id="state-select-label">State</InputLabel>
        <Select
          labelId="state-select-label"
          label="State"
          value={stateId}
          onChange={handleStateChange}
          disabled={loadingStates}
        >
          <MenuItem value=""><em>All states</em></MenuItem>
          {states.map((s) => (
            <MenuItem key={s.state_id} value={s.state_id}>{s.state_name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="w-full sm:w-[350px]">
        <InputLabel id="college-select-label">College</InputLabel>
        <Select
          labelId="college-select-label"
          label="College"
          value={instituteId}
          onChange={handleCollegeChange}
          disabled={!stateId || loadingColleges}
          MenuProps={{ PaperProps: { style: { maxHeight: 350 } } }}
        >
          <MenuItem value=""><em>All colleges</em></MenuItem>
          {colleges.map((c) => {
            const isActive = String(c.is_active) === "1" || String(c.is_active).toLowerCase() === "true";
            return (
              <MenuItem key={c.institute_id} value={c.institute_id}>
                <div className="flex w-full items-center justify-between gap-4">
                  <span className="truncate" title={c.institute_name}>
                    {c.institute_name}
                  </span>
                  <Chip
                    size="small"
                    label={isActive ? "Active" : "Inactive"}
                    color={isActive ? "success" : "error"}
                    variant={isActive ? "filled" : "outlined"}
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, minWidth: 60 }}
                  />
                </div>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <div className="flex items-center pt-1 sm:pt-1">
        <Button
          variant="contained"
          onClick={onSearch}
          disabled={!stateId || loadingStates || loadingColleges}
          sx={{ height: 40 }}
        >
          Search
        </Button>
      </div>

      {(loadingStates || loadingColleges) && (
        <div className="flex items-center pt-2 sm:pt-3">
          <CircularProgress size={20} />
        </div>
      )}

      {error && <Alert severity="error" className="w-full sm:w-auto">{error}</Alert>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PeopleResultsTable Component
// ---------------------------------------------------------------------------
function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

function stringToColor(string) {
  if (!string) return '#1976d2';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function PeopleResultsTable({ people, loading }) {
  const [activeRole, setActiveRole] = useState("All");
  const [search, setSearch] = useState("");

  const roleTabs = useMemo(() => {
    const roles = Array.from(new Set(people.map((p) => p.role_name))).sort();
    return ["All", ...roles];
  }, [people]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      const roleMatch = activeRole === "All" || p.role_name === activeRole;
      if (!roleMatch) return false;
      if (!q) return true;
      return (
        p.name?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.designation?.toLowerCase().includes(q)
      );
    });
  }, [people, activeRole, search]);

  return (
    <Paper elevation={0} className="overflow-hidden border border-slate-200 rounded-xl shadow-sm">
      <div className="flex flex-col gap-3 border-b border-slate-200 p-3 bg-white sm:flex-row sm:items-center sm:justify-between">
        <Tabs 
          value={activeRole} 
          onChange={(_, v) => setActiveRole(v)} 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minWidth: 100 } }}
        >
          {roleTabs.map((role) => (
            <Tab key={role} value={role} label={role === "All" ? `All (${people.length})` : `${role} (${people.filter((p) => p.role_name === role).length})`} />
          ))}
        </Tabs>
        <TextField 
          size="small" 
          placeholder="Search name, email, designation…" 
          value={search} 
          onChange={(e) => setSearch(e.target.value)} 
          className="w-full sm:w-72" 
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>) }} 
        />
      </div>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {['Name', 'Role', 'Status', 'Contact', 'Designation', 'Institute ID', 'First login', 'Last login'].map((head) => (
                <TableCell key={head} sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc', py: 1.5, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-16 text-center text-slate-500 text-sm">No people match this filter.</TableCell></TableRow>
            )}
            {filtered.map((p) => (
              <TableRow key={`${p.role_name}-${p.id}`} hover sx={{ '&:last-child td, &:last-child th': { border: 0 }, transition: 'background-color 0.2s' }}>
                <TableCell sx={{ py: 1.5 }}>
                  <div className="flex items-center gap-3">
                    <Avatar sx={{ width: 34, height: 34, fontSize: '0.85rem', bgcolor: stringToColor(p.name), color: 'white', fontWeight: 600, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{initials(p.name)}</Avatar>
                    <span className="font-semibold text-slate-800 text-sm">{p.name}</span>
                  </div>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Chip size="small" label={p.role_name} sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#f1f5f9', color: '#475569', borderRadius: 1.5 }} />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Chip 
                    size="small" 
                    label={String(p.status) === "1" || String(p.status).toLowerCase() === "true" ? "Active" : "Inactive"} 
                    color={String(p.status) === "1" || String(p.status).toLowerCase() === "true" ? "success" : "error"} 
                    variant={String(p.status) === "1" || String(p.status).toLowerCase() === "true" ? "filled" : "outlined"} 
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, minWidth: 60 }} 
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <div className="flex flex-col text-xs gap-0.5">
                    <span className="font-medium text-slate-700">{p.email}</span>
                    <span className="text-slate-500">{p.mobile || "—"}{p.whatsapp_number ? ` · WA: ${p.whatsapp_number}` : ""}</span>
                  </div>
                </TableCell>
                <TableCell sx={{ py: 1.5, fontSize: '0.8rem', color: 'text.secondary' }}>{p.designation || "—"}</TableCell>
                <TableCell sx={{ py: 1.5, fontSize: '0.8rem', color: 'text.secondary' }}>{p.institute_id ?? "—"}</TableCell>
                <TableCell className="whitespace-nowrap" sx={{ py: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>{formatDate(p.first_login)}</TableCell>
                <TableCell className="whitespace-nowrap" sx={{ py: 1.5, fontSize: '0.75rem', color: 'text.secondary' }}>{formatDate(p.last_login)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function LeadersSpocsFacultyPage() {
  const [people, setPeople] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [peopleError, setPeopleError] = useState("");
  const [filters, setFilters] = useState({ stateId: null, instituteId: null });

  const handleSearch = () => {
    if (!filters.stateId) {
      setPeople([]);
      return;
    }
    setLoadingPeople(true);
    setPeopleError("");
    fetchLeadersSpocsFaculty({ stateId: filters.stateId, instituteId: filters.instituteId })
      .then((data) => setPeople(data))
      .catch(() => setPeopleError("Couldn't load leaders / SPOCs / faculty. Try again."))
      .finally(() => setLoadingPeople(false));
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Leaders, SPOCs &amp; Faculty</h1>
        <p className="text-sm text-slate-500">Pick a state, then optionally narrow to a single college.</p>
      </div>

      <StateCollegeFilter onChange={setFilters} onSearch={handleSearch} />

      {peopleError && <Alert severity="error">{peopleError}</Alert>}
      
      {loadingPeople ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
          <CircularProgress size={22} />
          <span>Loading…</span>
        </div>
      ) : (
        <PeopleResultsTable people={people} loading={loadingPeople} />
      )}
    </div>
  );
}